// The run block at the tail of every snapshot: which island, what phase, the boss's parts,
// the train/bell state and the livestock. About 30 bytes plus 11 per animal; the boss's payload
// is written by its own def (sim/bosses).

import { Reader, Writer } from './codec';
import type { Game } from './game';
import { CState } from './critters';
import { bonusDoorOpen } from './run';
import { PU } from './powerups';
import { BossKind, BossNet } from './bosses/types';
import { bossDef } from './bosses/index';
export type { BossNet } from './bosses/types';

export interface CritterNet { id: number; type: number; x: number; y: number; z: number; yaw: number; st: number; hp: number }
export interface RunSnap {
  phase: number; village: number; island: number; index: number; timer: number;
  wave: number; waves: number; warpBy: number; bellBy: number; boardMask: number;
  /** the boss fight's own payload (sim/bosses/<boss>.ts dec; narrow on .kind) */
  boss: BossNet | null;
  /** the boss this island holds (fight or not yet): clients load its model during the breather */
  bossKind: BossKind;
  critters: CritterNet[];
  /** on the bonus island / its red doors are open on this one; the gauntlet clock (ticks) */
  bonus: boolean; bonusOpen: boolean; clock: number;
  /** the warp under way goes to the bonus island */
  warpBonus: boolean;
  /** the chase (CHASE.md): warping back a village island with the Leviathan behind; the swing telegraph is running */
  warpBack: boolean; swing: boolean;
  /** power-up drops lying on the island (el = element, -1 none) */
  pickups: PickupNet[];
  /** running timed buffs, for everyone's auras (el = element, -1 none; t = seconds left) */
  buffs: BuffNet[];
}
export interface PickupNet { id: number; type: number; el: number; x: number; y: number; z: number }
export interface BuffNet { pid: number; type: number; el: number; t: number; over: boolean }

export function encRun(w: Writer, g: Game) {
  const r = g.run;
  w.u8w(r.phase); w.u8w(r.village); w.u8w(r.island); w.u16w(g.world.index);
  w.u16w(Math.max(0, Math.min(65535, r.timer))); w.u8w(r.wave); w.u8w(r.waves);
  w.u8w(r.warpBy < 0 ? 255 : r.warpBy); w.u8w(r.bellBy < 0 ? 255 : r.bellBy); w.u16w(r.boardMask & 0xffff);
  // the boss: its kind (0 none), then whatever its def writes
  const b = g.boss;
  if (b) { w.u8w(b.kind); bossDef(b.kind).enc(w, g, b); } else w.u8w(0);
  w.u8w(r.bossKind);
  const cs = g.critters;
  w.u8w(cs.length);
  for (const c of cs) {
    w.u8w(c.id); w.u8w(c.type); w.cmw(c.x); w.cmw(c.y); w.cmw(c.z); w.angw(c.yaw); w.u8w(c.st);
    w.u8w(c.st === CState.Dead ? 0 : Math.max(1, Math.round((c.hp / c.maxHp) * 255)));
  }
  w.u8w((r.bonus ? 1 : 0) | (bonusDoorOpen(g) ? 2 : 0) | (r.warpTo === 1 ? 4 : 0) | (r.warpTo === 2 ? 8 : 0) | (r.swingT > 0 ? 16 : 0)); w.u16w(Math.max(0, Math.min(65535, r.clock)));
  const ks = g.powerups.list;
  w.u8w(Math.min(255, ks.length));
  for (let i = 0; i < Math.min(255, ks.length); i++) {
    const k = ks[i];
    w.u8w(k.id); w.u8w(k.type); w.u8w(k.el + 1); w.cmw(k.x); w.cmw(k.y); w.cmw(k.z);
  }
  let nb = 0;
  for (const p of g.players.values()) if (p.puAct !== PU.None || p.over > 0 || p.stimT > 0) nb++;
  w.u8w(nb);
  for (const p of g.players.values()) {
    if (p.puAct === PU.None && p.over <= 0 && p.stimT <= 0) continue;
    const type = p.puAct !== PU.None ? p.puAct : p.stimT > 0 ? PU.Stim : PU.None;
    const t = p.puAct !== PU.None ? p.puT : p.stimT;
    w.u8w(p.id); w.u8w(type); w.u8w(p.puAct !== PU.None ? p.puActEl : 0); w.u8w(Math.min(255, Math.ceil(t / 60))); w.u8w(p.over > 0 ? 1 : 0);
  }
}

export function decRun(r: Reader): RunSnap {
  const s: RunSnap = {
    phase: r.u8(), village: r.u8(), island: r.u8(), index: r.u16(), timer: r.u16(), wave: r.u8(), waves: r.u8(),
    warpBy: r.u8(), bellBy: r.u8(), boardMask: r.u16(), boss: null, bossKind: BossKind.None, critters: [],
    bonus: false, bonusOpen: false, warpBonus: false, warpBack: false, swing: false, clock: 0, pickups: [], buffs: [],
  };
  const bk = r.u8() as BossKind;
  if (bk) s.boss = bossDef(bk).dec(r, bk);
  s.bossKind = r.u8() as BossKind;
  const n = r.u8();
  for (let i = 0; i < n; i++) {
    s.critters.push({ id: r.u8(), type: r.u8(), x: r.cm(), y: r.cm(), z: r.cm(), yaw: r.ang(), st: r.u8(), hp: r.u8() / 255 });
  }
  const bf = r.u8();
  s.bonus = (bf & 1) !== 0; s.bonusOpen = (bf & 2) !== 0; s.warpBonus = (bf & 4) !== 0; s.warpBack = (bf & 8) !== 0; s.swing = (bf & 16) !== 0; s.clock = r.u16();
  const nk = r.u8();
  for (let i = 0; i < nk; i++) s.pickups.push({ id: r.u8(), type: r.u8(), el: r.u8() - 1, x: r.cm(), y: r.cm(), z: r.cm() });
  const nb = r.u8();
  for (let i = 0; i < nb; i++) s.buffs.push({ pid: r.u8(), type: r.u8(), el: r.u8() - 1, t: r.u8(), over: r.u8() === 1 });
  return s;
}
